import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { BusinessCard } from '../types';

interface OBusinessCardProps {
  card: BusinessCard;
  className?: string;
  showActions?: boolean;
  onEdit?: () => void;
  onShare?: () => void;
  onPhotoUpload?: () => void;
  size?: 'small' | 'medium' | 'large';
  showQR?: boolean;
  qrCodeContent?: React.ReactNode;
  isFlipped?: boolean;
  onFlip?: () => void;
}

export const OBusinessCard: React.FC<OBusinessCardProps> = ({
  card,
  className = '',
  showActions = false,
  onEdit,
  onShare,
  onPhotoUpload,
  size = 'medium',
  showQR = false,
  qrCodeContent,
  isFlipped = false,
  onFlip
}) => {
  const [flipped, setFlipped] = useState(isFlipped);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleViewBack = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleFlip();
  };

  // Sync with external isFlipped prop
  useEffect(() => {
    setFlipped(isFlipped);
  }, [isFlipped]);

  const handleFlip = () => {
    const newFlippedState = !flipped;
    setFlipped(newFlippedState);
    if (onFlip) {
      onFlip();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to truncate text smartly
  const smartTruncate = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;

    // Try to break at word boundaries
    const truncated = text.substring(0, maxLength - 3);
    const lastSpace = truncated.lastIndexOf(' ');

    if (lastSpace > maxLength * 0.6) {
      return text.substring(0, lastSpace) + '...';
    }

    return truncated + '...';
  };

  // SVG-based curved text that follows the exact circle path with proper color support
  const SVGCurvedText: React.FC<{
    text: string;
    radius: number;
    startAngle: number;
    fontSize: number;
    maxArcDegrees?: number;
    reverse?: boolean;
    className?: string;
    fontFamily?: string;
    fontWeight?: number;
    color?: string;
  }> = ({
    text,
    radius,
    startAngle,
    fontSize,
    maxArcDegrees = 160,
    reverse = false,
    className = '',
    fontFamily = '"Helvetica Neue", Arial, sans-serif',
    fontWeight = 600,
    color = '#2c3e50'
  }) => {
    const svgSize = (radius + 50) * 2;
    const centerX = svgSize / 2;
    const centerY = svgSize / 2;

    // Calculate circumference and character spacing
    const circumference = 2 * Math.PI * radius;
    const maxArcLength = (maxArcDegrees / 360) * circumference;

    // Estimate character width (rough approximation)
    const avgCharWidth = fontSize * 0.6;
    const maxChars = Math.floor(maxArcLength / avgCharWidth);

    // Truncate text if needed
    let displayText = text;
    if (text.length > maxChars) {
      displayText = text.substring(0, maxChars - 3) + '...';
    }

    // Calculate the actual arc length needed for the text
    const textLength = displayText.length * avgCharWidth;
    const arcAngle = Math.min((textLength / circumference) * 360, maxArcDegrees);

    // Create SVG path for the text to follow
    const pathRadius = radius;
    const startAngleRad = ((startAngle - arcAngle / 2) * Math.PI) / 180;
    const endAngleRad = ((startAngle + arcAngle / 2) * Math.PI) / 180;

    const startX = centerX + pathRadius * Math.cos(startAngleRad);
    const startY = centerY + pathRadius * Math.sin(startAngleRad);
    const endX = centerX + pathRadius * Math.cos(endAngleRad);
    const endY = centerY + pathRadius * Math.sin(endAngleRad);

    const largeArcFlag = arcAngle > 180 ? 1 : 0;
    const sweepFlag = reverse ? 0 : 1;

    const pathData = `M ${startX} ${startY} A ${pathRadius} ${pathRadius} 0 ${largeArcFlag} ${sweepFlag} ${endX} ${endY}`;
    const pathId = `textPath-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{ width: svgSize, height: svgSize, color }}
      >
        <svg
          width={svgSize}
          height={svgSize}
          className={`overflow-visible ${className}`}
        >
          <defs>
            <path
              id={pathId}
              d={pathData}
              fill="none"
            />
          </defs>
          <text
            fontSize={fontSize}
            fontWeight={fontWeight}
            fontFamily={fontFamily}
            fill="currentColor"
            textAnchor="middle"
            dominantBaseline="middle"
          >
            <textPath
              href={`#${pathId}`}
              startOffset="50%"
              style={{ letterSpacing: '0.5px' }}
            >
              {displayText}
            </textPath>
          </text>
        </svg>
      </div>
    );
  };

  const sizes = {
    small: 280,
    medium: 350,
    large: 420
  };

  const cardSize = sizes[size];
  const radius = cardSize / 2;
  const profileSize = card.customization?.centerStyle?.profileCircleSize || 140;

  // Enhanced marketing-focused color schemes with white background
  const colorScheme = {
    background: card.backgroundColor || '#ffffff',
    backgroundSecondary: 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.02) 100%)',
    border: '#e2e8f0',
    text: card.textColor || '#2c3e50',
    accent: '#3b82f6',
    secondary: '#64748b',
    gradient: {
      primary: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
      accent: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
      success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      premium: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    },
    shadow: {
      primary: '0 10px 40px rgba(59, 130, 246, 0.15)',
      secondary: '0 4px 20px rgba(0, 0, 0, 0.08)',
      glass: '0 4px 16px rgba(0, 0, 0, 0.06)'
    },
    glass: {
      background: 'rgba(255, 255, 255, 0.8)',
      border: 'rgba(59, 130, 246, 0.2)',
      blur: 'blur(10px)'
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 0.8;
              transform: scale(1);
            }
            50% {
              opacity: 0.6;
              transform: scale(1.02);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-6px);
            }
          }

          .profile-glow {
            position: relative;
          }

          .profile-glow::before {
            content: '';
            position: absolute;
            top: -4px;
            left: -4px;
            right: -4px;
            bottom: -4px;
            border-radius: 50%;
            background: linear-gradient(45deg, #ff6b6b, #feca57, #48cae4, #4ecdc4);
            opacity: 0.7;
            filter: blur(10px);
            z-index: -1;
            animation: float 3s ease-in-out infinite;
          }

          /* Card Flip Animation */
          .card-flip-container {
            perspective: 1000px;
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
          }

          .card-flipper {
            position: relative;
            width: 100%;
            height: 100%;
            transition: transform 0.8s;
            transform-style: preserve-3d;
          }

          .card-flipper.flipped {
            transform: rotateY(180deg);
          }

          .card-front, .card-back {
            position: absolute;
            width: 100%;
            height: 100%;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            border-radius: 50%;
            overflow: hidden;
          }

          .card-back {
            transform: rotateY(180deg);
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #fff;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
            border: 3px solid rgba(59, 130, 246, 0.2);
            z-index: 2;
          }

          .qr-container {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 20px;
            background-color: #fff;
            border-radius: 50%;
            overflow: hidden;
          }

          .scan-text {
            margin-top: 15px;
            font-size: 14px;
            font-weight: 600;
            color: #64748b;
          }

          /* Mobile Responsive Styles for O Business Card */
          @media (max-width: 768px) {
            .o-business-card {
              width: 280px !important;
              max-width: 90vw !important;
            }
          }

          @media (max-width: 480px) {
            .o-business-card {
              width: 250px !important;
              max-width: 85vw !important;
            }
          }

          @media (max-width: 350px) {
            .o-business-card {
              width: 220px !important;
              max-width: 95vw !important;
            }
          }
        `}
      </style>
      <div
        className={`o-business-card flex flex-col mx-auto relative ${className}`}
        style={{ width: `${cardSize}px`, height: `${cardSize}px` }}
      >
        <div className="card-flip-container">
          <div ref={cardRef} className={`card-flipper ${flipped ? 'flipped' : ''}`}>
            <div className="card-front">
              <div
                className="w-full aspect-square rounded-full relative overflow-visible cursor-pointer transition-all duration-300"
                style={{
                  background: colorScheme.background,
                  boxShadow: colorScheme.shadow.primary,
                  border: `3px solid ${colorScheme.glass.border}`,
                  backdropFilter: colorScheme.glass.blur,
                  WebkitBackdropFilter: colorScheme.glass.blur,
                }}
                onMouseEnter={(e) => {
                  if (!flipped) {
                    e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                    e.currentTarget.style.boxShadow = colorScheme.shadow.primary.replace('0.3', '0.5');
                  }
                }}
                onMouseLeave={(e) => {
                  if (!flipped) {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = colorScheme.shadow.primary;
                  }
                }}
              >
                {/* Customizable circles with defaults */}
                {(card.customization?.circleStyle?.showOuter !== false) && (
                  <div
                    className="absolute rounded-full transition-all duration-300"
                    style={{
                      top: `${-15 - (card.customization?.circleStyle?.outerOffset || 15)}px`,
                      left: `${-15 - (card.customization?.circleStyle?.outerOffset || 15)}px`,
                      right: `${-15 - (card.customization?.circleStyle?.outerOffset || 15)}px`,
                      bottom: `${-15 - (card.customization?.circleStyle?.outerOffset || 15)}px`,
                      border: `${card.customization?.circleStyle?.outerBorderWidth || 2}px dotted ${colorScheme.glass.border}`,
                      opacity: card.customization?.circleStyle?.outerOpacity || 0.6,
                      background: colorScheme.backgroundSecondary,
                      backdropFilter: colorScheme.glass.blur,
                      WebkitBackdropFilter: colorScheme.glass.blur,
                      animation: 'pulse 3s ease-in-out infinite',
                    }}
                  />
                )}

                {(card.customization?.circleStyle?.showInner !== false) && (
                  <div
                    className="absolute rounded-full"
                    style={{
                      top: `${card.customization?.circleStyle?.innerRadius || 15}px`,
                      left: `${card.customization?.circleStyle?.innerRadius || 15}px`,
                      right: `${card.customization?.circleStyle?.innerRadius || 15}px`,
                      bottom: `${card.customization?.circleStyle?.innerRadius || 15}px`,
                      border: `${card.customization?.circleStyle?.innerBorderWidth || 1}px solid ${colorScheme.glass.border}`,
                      opacity: card.customization?.circleStyle?.innerOpacity || 0.3,
                      background: colorScheme.glass.background,
                      backdropFilter: colorScheme.glass.blur,
                      WebkitBackdropFilter: colorScheme.glass.blur,
                    }}
                  />
                )}

                {/* SVG-based curved text with customizable positioning */}
                <div className="relative w-full h-full" style={{ color: colorScheme.text }}>
                  {/* Company name at top - using custom styles */}
                  <SVGCurvedText
                    text={card.company || card.title || 'BUSINESS PROFESSIONAL'}
                    radius={card.customization?.companyStyle?.radius || (radius - 35)}
                    startAngle={card.customization?.companyStyle?.startAngle || -90}
                    fontSize={card.customization?.companyStyle?.fontSize || 16}
                    maxArcDegrees={card.customization?.companyStyle?.maxArcDegrees || 140}
                    fontFamily={card.customization?.companyStyle?.fontFamily}
                    fontWeight={card.customization?.companyStyle?.fontWeight || 600}
                    color={card.customization?.companyStyle?.color || colorScheme.text}
                  />

                  {/* Contact info at bottom - using custom styles */}
                  <SVGCurvedText
                    text={`${card.email} • ${card.phone}`}
                    radius={card.customization?.contactStyle?.radius || (radius - 35)}
                    startAngle={card.customization?.contactStyle?.startAngle || 90}
                    fontSize={card.customization?.contactStyle?.fontSize || 13}
                    maxArcDegrees={card.customization?.contactStyle?.maxArcDegrees || 140}
                    fontFamily={card.customization?.contactStyle?.fontFamily}
                    fontWeight={card.customization?.contactStyle?.fontWeight || 500}
                    color={card.customization?.contactStyle?.color || colorScheme.text}
                  />

                  {/* Website - using custom styles */}
                  {card.website && (
                    <SVGCurvedText
                      text={card.website.replace('https://', '').replace('http://', '')}
                      radius={card.customization?.websiteStyle?.radius || (radius - 45)}
                      startAngle={card.customization?.websiteStyle?.startAngle || 180}
                      fontSize={card.customization?.websiteStyle?.fontSize || 12}
                      maxArcDegrees={card.customization?.websiteStyle?.maxArcDegrees || 80}
                      fontFamily={card.customization?.websiteStyle?.fontFamily}
                      fontWeight={card.customization?.websiteStyle?.fontWeight || 500}
                      color={card.customization?.websiteStyle?.color || colorScheme.text}
                    />
                  )}

                  {/* Additional contact info - using custom styles */}
                  {(card.address || card.linkedin) && (
                    <SVGCurvedText
                      text={card.address || card.linkedin || ''}
                      radius={card.customization?.additionalStyle?.radius || (radius - 45)}
                      startAngle={card.customization?.additionalStyle?.startAngle || 0}
                      fontSize={card.customization?.additionalStyle?.fontSize || 12}
                      maxArcDegrees={card.customization?.additionalStyle?.maxArcDegrees || 80}
                      fontFamily={card.customization?.additionalStyle?.fontFamily}
                      fontWeight={card.customization?.additionalStyle?.fontWeight || 500}
                      color={card.customization?.additionalStyle?.color || colorScheme.text}
                    />
                  )}
                </div>

                {/* Center content - Profile or QR code - ALWAYS CENTERED */}
                <div
                  className="absolute flex flex-col items-center justify-center text-center z-10"
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: `translate(${-50 + (card.customization?.centerStyle?.centerOffset?.x || 0)}%, ${-50 + (card.customization?.centerStyle?.centerOffset?.y || 0)}%)`
                  }}
                >
                  <div
                    className="profile-glow rounded-full flex items-center justify-center mb-2 relative transition-all duration-300"
                    style={{
                      width: `${profileSize}px`,
                      height: `${profileSize}px`,
                      background: showQR ? 'white' : colorScheme.gradient.accent,
                      boxShadow: colorScheme.shadow.glass,
                      border: `3px solid ${colorScheme.glass.border}`,
                      backdropFilter: colorScheme.glass.blur,
                      WebkitBackdropFilter: colorScheme.glass.blur,
                    }}
                  >
                    {showQR ? (
                      <div className="hidden">
                        QR CODE
                      </div>
                    ) : card.profileImage ? (
                      <div className="relative w-full h-full">
                        <img
                          src={card.profileImage}
                          alt={card.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                        {onPhotoUpload && (
                          <div
                            onClick={onPhotoUpload}
                            className="absolute bottom-[5px] right-[5px] bg-black/70 rounded-full w-6 h-6 flex items-center justify-center cursor-pointer text-xs transition-all duration-200 text-white hover:bg-blue-500/90 hover:scale-110"
                          >
                            📷
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="relative w-full h-full flex items-center justify-center">
                        <span
                          className="font-bold text-white uppercase"
                          style={{ fontSize: `${Math.round(profileSize * 0.3)}px` }}
                        >
                          {getInitials(card.name)}
                        </span>
                        {onPhotoUpload && (
                          <div
                            onClick={onPhotoUpload}
                            className="absolute bottom-[5px] right-[5px] bg-blue-500/90 rounded-full w-7 h-7 flex items-center justify-center cursor-pointer text-sm text-white border-2 border-white shadow-md transition-all duration-200 hover:bg-blue-500 hover:scale-110"
                          >
                            📷
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Company Logo - positioned strategically */}
                {card.logo && (
                  <div className="absolute top-5 right-5 w-10 h-10 rounded-lg overflow-hidden bg-white/90 flex items-center justify-center shadow-md z-20">
                    <img
                      src={card.logo}
                      alt={`${card.company} logo`}
                      loading="lazy"
                      className="max-w-[32px] max-h-[32px] object-contain"
                      onError={(e) => {
                        // Hide logo if it fails to load
                        e.currentTarget.parentElement!.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Name positioning - SEPARATE from center circle */}
                <div
                  className="absolute w-full flex flex-col items-center z-[15]"
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: (card.customization?.centerStyle?.nameCurved ?? true)
                      ? `translate(${-50 + (card.customization?.centerStyle?.centerOffset?.x || 0) + (card.customization?.centerStyle?.nameHorizontalOffset || 0)}%, ${-50 + (card.customization?.centerStyle?.centerOffset?.y || 0) - 70 + (card.customization?.centerStyle?.nameVerticalOffset || 0)}%)`
                      : `translate(${-50 + (card.customization?.centerStyle?.centerOffset?.x || 0) + (card.customization?.centerStyle?.nameHorizontalOffset || 0)}%, ${-50 + (card.customization?.centerStyle?.centerOffset?.y || 0) + 70 + (card.customization?.centerStyle?.nameVerticalOffset || 0)}%)`,
                  }}
                >
                  {/* Name prominently displayed - straight or curved */}
                  {(card.customization?.centerStyle?.nameCurved ?? true) ? (
                    <SVGCurvedText
                      text={smartTruncate(card.name, 20)}
                      radius={card.customization?.centerNameStyle?.radius || (radius - 35)}
                      startAngle={card.customization?.centerNameStyle?.startAngle || -90}
                      fontSize={card.customization?.centerNameStyle?.fontSize || 18}
                      maxArcDegrees={card.customization?.centerNameStyle?.maxArcDegrees || 140}
                      fontFamily={card.customization?.centerNameStyle?.fontFamily}
                      fontWeight={card.customization?.centerNameStyle?.fontWeight || 700}
                      color={card.customization?.centerNameStyle?.color || colorScheme.text}
                    />
                  ) : (
                    <div
                      className="text-center max-w-[200px] leading-tight overflow-hidden text-ellipsis whitespace-nowrap"
                      style={{
                        fontSize: `${card.customization?.centerNameStyle?.fontSize || 18}px`,
                        fontWeight: card.customization?.centerNameStyle?.fontWeight || 700,
                        fontFamily: card.customization?.centerNameStyle?.fontFamily || '"Helvetica Neue", Arial, sans-serif',
                        color: card.customization?.centerNameStyle?.color || colorScheme.text,
                      }}
                    >
                      {smartTruncate(card.name, 20)}
                    </div>
                  )}
                </div>

                {/* Brand text - positioned outside main circle */}
                {(card.customization?.centerStyle?.showBranding !== false || !card.isPremium) && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 text-[10px] font-medium uppercase tracking-wider opacity-70 z-[5]"
                    style={{
                      bottom: '-25px',
                      color: colorScheme.secondary,
                    }}
                  >
                    OBusinessCard.com
                  </div>
                )}
              </div>
            </div>
            <div className="card-back">
              <div className="qr-container bg-white rounded-full p-5 w-full h-full flex flex-col items-center justify-center relative">
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <div className="w-[200px] h-[200px] flex items-center justify-center mb-2.5 bg-white p-2.5 rounded-lg">
                    {qrCodeContent || (
                      <QRCode
                        value={`${window.location.origin}/card/${card.id || (card as any)._id || 'demo'}`}
                        size={180}
                        level="H"
                        className="max-w-full h-auto"
                      />
                    )}
                  </div>
                  <div className="scan-text text-xs text-gray-500 font-semibold text-center">
                    📱 Scan to exchange cards
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* View Back Button */}
        <div className="absolute bottom-5 right-5 z-10 flex justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewBack(e);
            }}
            className="bg-slate-800/90 text-white border border-blue-500 rounded-[20px] px-4 py-2 text-sm cursor-pointer flex items-center gap-1.5 transition-all duration-200 shadow-lg hover:bg-blue-900/90 hover:-translate-y-0.5"
          >
            <span>🔄</span> {flipped ? 'View Front' : 'View Back'}
          </button>
        </div>
      </div>
    </>
  );
};
